/* v77.99.108 (iter 122.m4b) — Public Facilitator Portal.
 *
 * Unauthenticated landing page reached via a 30-day single-use magic link
 * (`/facilitator/:token`). A third party (contact-centre supervisor,
 * mediator, therapist, social worker, etc.) submits an immutable,
 * evidence-grade observation of a contact visit.
 *
 * Design constraints (user spec):
 *  - No accounts, no login.
 *  - Read-only visit context shown above the form so the observer knows
 *    what they are commenting on.
 *  - Structured form: Arrival · Departure · Child presentation · Concerns ·
 *    Safeguarding (toggle + details) · Declaration + typed signature.
 *  - Drafts auto-save to localStorage with a 24-hour TTL so the observer
 *    can refresh / navigate away / come back without losing input.
 *  - On submit the record is locked forever (immutable). The thank-you
 *    screen shows the submission_id + audit_id for their own records.
 *  - All UI uses the design system tokens; no third-party brand chrome.
 *
 * The component does not call any other authenticated routes and the URL
 * is intentionally outside `/app/*` so it cannot leak the parent's
 * session.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ShieldCheck, AlertTriangle, CheckCircle2, Clock, MapPin, Users, FileText,
  Lock, Info, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours per user spec.

const ROLE_LABELS = {
  contact_supervisor: "Contact-centre supervisor",
  mediator:           "Mediator",
  therapist:          "Therapist / counsellor",
  teacher:            "Teacher",
  social_worker:      "Social worker",
  family_member:      "Family member",
  other:              "Other",
};

const TYPE_LABEL = {
  parent_to_parent_handover: "Parent-to-parent handover",
  supervised_contact:        "Supervised contact",
  unsupervised_contact:      "Unsupervised contact",
  overnight_contact:         "Overnight contact",
  video_call:                "Video call",
  school_event:              "School event",
  medical_appointment:       "Medical appointment",
  other:                     "Other",
};

const draftKey = (token) => `cpc.facilitator.draft.${token}`;

function readDraft(token) {
  try {
    const raw = localStorage.getItem(draftKey(token));
    if (!raw) return null;
    const { saved_at, payload } = JSON.parse(raw);
    if (!saved_at || !payload) return null;
    if (Date.now() - saved_at > DRAFT_TTL_MS) {
      localStorage.removeItem(draftKey(token));
      return null;
    }
    return payload;
  } catch { return null; }
}

function writeDraft(token, payload) {
  try {
    localStorage.setItem(
      draftKey(token),
      JSON.stringify({ saved_at: Date.now(), payload }),
    );
  } catch { /* localStorage may be disabled */ }
}

function clearDraft(token) {
  try { localStorage.removeItem(draftKey(token)); } catch { /* ignore */ }
}

const empty = (invite, visit) => ({
  observer_name: invite?.name || "",
  observer_email: invite?.email || "",
  observer_phone: "",
  observer_organisation: invite?.organisation || "",
  observer_role: invite?.role || "contact_supervisor",
  observed_at: visit?.scheduled_start || new Date().toISOString().slice(0, 16),
  arrival_observation: "",
  departure_observation: "",
  child_presentation: "",
  concerns_observed: "",
  additional_observations: "",
  safeguarding_flag: false,
  safeguarding_details: "",
  declaration_signed: false,
  signature_typed_name: "",
});

export default function FacilitatorPortal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [bootstrapError, setBootstrapError] = useState(null);
  const [context, setContext] = useState(null);
  const [form, setForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);

  // ---- Bootstrap: fetch context + restore draft -----------------------------
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/facilitator-submission/${token}`);
        const data = r.data || {};
        setContext(data);
        if (data.status === "submitted") {
          setSubmitted({
            submission_id: data.submission_id,
            submitted_at: data.submitted_at,
            already: true,
          });
          clearDraft(token);
          setLoading(false);
          return;
        }
        // Pending — initialise the form; restore draft if any.
        const initial = empty(data.invite, data.visit);
        const draft = readDraft(token);
        if (draft && typeof draft === "object") {
          setForm({ ...initial, ...draft });
          setDraftRestored(true);
        } else {
          setForm(initial);
        }
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const code = (detail && typeof detail === "object" && detail.error) || null;
        setBootstrapError({
          code,
          message: (detail && typeof detail === "object" && detail.message)
            || err.message || "Unable to open this link.",
          status: err?.response?.status,
        });
      } finally {
        setLoading(false);
      }
    })();
     
  }, [token]);

  // ---- Auto-save: every 800ms write the current form to localStorage -------
  useEffect(() => {
    if (!form || submitted) return;
    const t = setTimeout(() => writeDraft(token, form), 800);
    return () => clearTimeout(t);
  }, [form, token, submitted]);

  // ---- Mark "started" the first time the observer interacts with the form --
  // The parent UI uses this for richer lifecycle status (Invited → Opened →
  // Started → Submitted). Fire-and-forget; idempotent on the server.
  const [startedSignalled, setStartedSignalled] = useState(false);
  const signalStarted = useCallback(() => {
    if (startedSignalled || !token || submitted) return;
    setStartedSignalled(true);
    axios.post(`${API}/public/facilitator-submission/${token}/start`).catch(() => {
      /* idempotent, best-effort — never block the form */
    });
  }, [startedSignalled, token, submitted]);

  const update = useCallback((patch) => {
    setForm((f) => (f ? { ...f, ...patch } : f));
  }, []);

  const visit = context?.visit;
  const visitWindow = useMemo(() => {
    if (!visit) return "";
    const start = (visit.scheduled_start || "").slice(11, 16);
    const end   = (visit.scheduled_end || "").slice(11, 16);
    if (!start && !end) return "";
    if (start && end) return `${start} – ${end}`;
    return start || end;
  }, [visit]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form) return;
    setSubmitError(null);
    // Client-side guards mirror the server (give faster feedback).
    if (!form.declaration_signed) {
      setSubmitError("Please confirm the declaration before submitting.");
      return;
    }
    if (form.safeguarding_flag && !(form.safeguarding_details || "").trim()) {
      setSubmitError("Please describe the safeguarding concern.");
      return;
    }
    if (!form.signature_typed_name.trim()) {
      setSubmitError("Please type your name to sign.");
      return;
    }
    const required = ["observer_name", "observer_email", "arrival_observation", "departure_observation", "child_presentation"];
    for (const k of required) {
      if (!(form[k] || "").trim()) {
        setSubmitError("Please fill in every required field marked with *.");
        return;
      }
    }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/public/facilitator-submission/${token}`, form,
      );
      setSubmitted({
        submission_id: r.data?.submission_id,
        submitted_at:  r.data?.submitted_at,
        audit_id:      r.data?.audit_id,
        already: false,
      });
      clearDraft(token);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") setSubmitError(detail);
      else if (detail?.message) setSubmitError(detail.message);
      else setSubmitError("Submission failed. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Render branches =====================================================
  if (loading) {
    return <PortalShell><LoadingPanel /></PortalShell>;
  }
  if (bootstrapError) {
    return <PortalShell><ErrorPanel error={bootstrapError} /></PortalShell>;
  }
  if (submitted) {
    return (
      <PortalShell>
        <SubmittedPanel submitted={submitted} visit={visit} />
      </PortalShell>
    );
  }
  if (!form || !context) return <PortalShell><LoadingPanel /></PortalShell>;

  return (
    <PortalShell>
      <header className="mb-6">
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] text-[#116B6E] font-medium mb-1.5">
          <Lock className="w-3 h-3" /> Secure single-use link · expires {(context.invite?.expires_at || "").slice(0, 10)}
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#2E3A3F]" data-testid="fac-title">
          Submit a third-party observation
        </h1>
        <p className="text-sm text-[#6F828A] mt-1.5 leading-snug">
          You have been invited to provide an independent record of a contact visit.
          Your submission is locked once you submit it — no edits, no deletions.
        </p>
        {draftRestored && (
          <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-[#0F5658] bg-[#D6F0EF] border border-[#A7D8DA] rounded-sm px-2 py-1" data-testid="fac-draft-restored">
            <Info className="w-3 h-3" /> A draft from this device was restored.
          </div>
        )}
      </header>

      <VisitContextCard visit={visit} visitWindow={visitWindow} invite={context.invite} />

      <form onSubmit={submit} onFocus={signalStarted} className="mt-6 space-y-6" data-testid="fac-form">
        <Section title="Your details" testId="fac-section-identity">
          <Two>
            <Field label="Your full name *" testId="fac-field-name">
              <Input value={form.observer_name}
                onChange={(e) => update({ observer_name: e.target.value })}
                placeholder="As you would sign on a witness statement"
                data-testid="fac-input-name"
                required maxLength={120} />
            </Field>
            <Field label="Your role *" testId="fac-field-role">
              <select value={form.observer_role}
                onChange={(e) => update({ observer_role: e.target.value })}
                className="w-full h-10 px-3 rounded-sm border border-[#D9E1E3] bg-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F9FA4]/55"
                data-testid="fac-input-role">
                {(context.observer_roles || Object.keys(ROLE_LABELS)).map((k) => (
                  <option key={k} value={k}>{ROLE_LABELS[k] || k}</option>
                ))}
              </select>
            </Field>
          </Two>
          <Two>
            <Field label="Email *" testId="fac-field-email">
              <Input type="email" value={form.observer_email}
                onChange={(e) => update({ observer_email: e.target.value })}
                data-testid="fac-input-email"
                required />
            </Field>
            <Field label="Phone (optional)">
              <Input value={form.observer_phone}
                onChange={(e) => update({ observer_phone: e.target.value })}
                data-testid="fac-input-phone" />
            </Field>
          </Two>
          <Field label="Organisation (optional)">
            <Input value={form.observer_organisation}
              onChange={(e) => update({ observer_organisation: e.target.value })}
              placeholder="Contact centre, mediation service, school, etc."
              data-testid="fac-input-org" />
          </Field>
        </Section>

        <Section title="Observation" testId="fac-section-observation">
          <Field label="Observed at" hint="ISO date-time. Pre-filled from the scheduled visit window.">
            <Input type="datetime-local"
              value={(form.observed_at || "").slice(0, 16)}
              onChange={(e) => update({ observed_at: e.target.value })}
              data-testid="fac-input-observed-at" />
          </Field>
          <Field label="Arrival *" hint="What did you observe at the start of the visit?">
            <Textarea value={form.arrival_observation}
              onChange={(e) => update({ arrival_observation: e.target.value })}
              rows={3} maxLength={4000}
              data-testid="fac-input-arrival"
              placeholder="e.g. Father arrived 5 minutes early, calm. Child greeted him without distress." />
          </Field>
          <Field label="Departure *" hint="What did you observe at the end of the visit / handover back?">
            <Textarea value={form.departure_observation}
              onChange={(e) => update({ departure_observation: e.target.value })}
              rows={3} maxLength={4000}
              data-testid="fac-input-departure"
              placeholder="e.g. Visit ended on time. Child waved goodbye, no signs of distress." />
          </Field>
          <Field label="Child presentation *" hint="The child's mood, behaviour, well-being during the visit.">
            <Textarea value={form.child_presentation}
              onChange={(e) => update({ child_presentation: e.target.value })}
              rows={3} maxLength={4000}
              data-testid="fac-input-presentation"
              placeholder="e.g. Engaged and chatty, played with toys throughout, ate lunch normally." />
          </Field>
          <Field label="Concerns observed (optional)" hint="Anything you'd flag for the wider record.">
            <Textarea value={form.concerns_observed}
              onChange={(e) => update({ concerns_observed: e.target.value })}
              rows={2} maxLength={4000}
              data-testid="fac-input-concerns" />
          </Field>
          <Field label="Additional observations (optional)">
            <Textarea value={form.additional_observations}
              onChange={(e) => update({ additional_observations: e.target.value })}
              rows={2} maxLength={4000}
              data-testid="fac-input-additional" />
          </Field>
        </Section>

        <Section title="Safeguarding" testId="fac-section-safeguarding"
          intro="Raise a safeguarding flag only when something happened that, in your professional judgement, should be brought to the attention of a safeguarding authority. This flag is recorded permanently.">
          <label className="inline-flex items-start gap-2.5 cursor-pointer" data-testid="fac-safeguarding-toggle">
            <Checkbox checked={form.safeguarding_flag}
              onCheckedChange={(v) => update({ safeguarding_flag: !!v })}
              data-testid="fac-input-safeguarding-flag" />
            <span className="text-sm text-[#2E3A3F]">
              I am raising a safeguarding concern about this visit.
            </span>
          </label>
          {form.safeguarding_flag && (
            <div className="mt-3">
              <Field label="Describe the safeguarding concern *">
                <Textarea value={form.safeguarding_details}
                  onChange={(e) => update({ safeguarding_details: e.target.value })}
                  rows={4} maxLength={4000}
                  data-testid="fac-input-safeguarding-details" />
              </Field>
            </div>
          )}
        </Section>

        <Section title="Declaration & signature" testId="fac-section-declaration">
          <div className="p-3 rounded-sm border border-[#D9E1E3] bg-[#F4FAFA] text-sm text-[#2E3A3F] leading-relaxed">
            I confirm that the above observation is a true and accurate record of
            what I observed at this contact visit, made without prompting or
            instruction, and that I am willing to repeat it to a court if asked.
          </div>
          <label className="mt-3 inline-flex items-start gap-2.5 cursor-pointer" data-testid="fac-declaration-toggle">
            <Checkbox checked={form.declaration_signed}
              onCheckedChange={(v) => update({ declaration_signed: !!v })}
              data-testid="fac-input-declaration" />
            <span className="text-sm text-[#2E3A3F]">I confirm the declaration above.</span>
          </label>
          <Field label="Typed signature (your full name) *">
            <Input value={form.signature_typed_name}
              onChange={(e) => update({ signature_typed_name: e.target.value })}
              placeholder="Type your name as your signature"
              data-testid="fac-input-signature"
              required maxLength={120} />
          </Field>
        </Section>

        {submitError && (
          <div className="p-3 rounded-sm border border-rose-200 bg-rose-50 text-sm text-rose-900 flex items-start gap-2" data-testid="fac-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{submitError}</span>
          </div>
        )}

        <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-2.5 sm:justify-between">
          <p className="text-[11px] text-[#6F828A] leading-snug max-w-md">
            Submitting locks this record. Your IP, browser and submission time are saved
            alongside the observation for court readiness.
          </p>
          <Button type="submit" disabled={submitting}
            className="bg-[#1F9FA4] hover:bg-[#178085] text-white rounded-sm h-11 px-6 text-sm font-medium"
            data-testid="fac-submit">
            {submitting
              ? <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</span>
              : <>Submit observation</>}
          </Button>
        </div>
      </form>
    </PortalShell>
  );
}

// ===== Sub-components ========================================================

function PortalShell({ children }) {
  return (
    <div className="min-h-screen bg-[#F4FAFA]" data-testid="facilitator-portal">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="flex items-center gap-2 text-[#0F5658] mb-6">
          <ShieldCheck className="w-5 h-5" strokeWidth={1.7} />
          <span className="text-sm font-medium">CoParent Console · Secure submission</span>
        </div>
        <div className="bg-white border border-[#D9E1E3] rounded-md p-5 sm:p-7">
          {children}
        </div>
        <p className="text-[11px] text-[#6F828A] mt-6 leading-snug">
          You received this link because a parent specifically invited you. This page is not part of an account
          and cannot be used to view anyone else's data. Questions? Email
          {" "}<a className="underline" href="mailto:info@coparentconsole.com">info@coparentconsole.com</a>.
        </p>
      </div>
    </div>
  );
}

function LoadingPanel() {
  return (
    <div className="py-10 text-center text-[#6F828A]" data-testid="fac-loading">
      <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      <p className="text-sm mt-2">Opening secure submission…</p>
    </div>
  );
}

function ErrorPanel({ error }) {
  const code = error.code;
  const friendly = code === "expired"
    ? "This invite has expired. Ask the parent to send you a new link."
    : code === "revoked"
      ? "This invite was revoked by the parent. They will need to send you a new link if they still want a record."
      : code === "invalid_token" || error.status === 404
        ? "This link is not valid. It may have been mistyped — please open the original email link directly."
        : error.message || "This link could not be opened.";
  return (
    <div className="py-6" data-testid="fac-error-panel">
      <div className="flex items-center gap-2 text-rose-700">
        <AlertTriangle className="w-5 h-5" />
        <span className="text-sm font-medium">Unable to open this submission</span>
      </div>
      <p className="text-sm text-[#2E3A3F] mt-3 leading-relaxed">{friendly}</p>
    </div>
  );
}

function SubmittedPanel({ submitted, visit }) {
  return (
    <div className="py-3" data-testid="fac-submitted-panel">
      <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] text-[#0F5658] font-medium mb-1">
        <CheckCircle2 className="w-3.5 h-3.5" /> Submitted · locked
      </div>
      <h1 className="font-heading text-2xl font-medium text-[#2E3A3F]">
        {submitted.already ? "Already submitted" : "Thank you — your observation is on file."}
      </h1>
      <p className="text-sm text-[#6F828A] mt-2 leading-snug">
        {submitted.already
          ? "This invite has already been used. A single observation has been submitted and is locked permanently."
          : "Your observation has been saved as an immutable record. The parent who invited you will see it on their contact visit timeline. No edits or deletions are possible."}
      </p>
      {visit && (
        <div className="mt-5 text-xs text-[#6F828A] grid grid-cols-2 gap-2 font-mono-data tabular-nums">
          <div><span className="text-[#9DB0B5]">Visit ref</span><br />{visit.evidence_ref || visit.id?.slice(0, 8)}</div>
          <div><span className="text-[#9DB0B5]">Visit date</span><br />{visit.visit_date}</div>
        </div>
      )}
      {submitted.submission_id && (
        <div className="mt-5 text-xs text-[#6F828A] grid grid-cols-1 sm:grid-cols-2 gap-2 font-mono-data tabular-nums">
          <div><span className="text-[#9DB0B5]">Submission ID</span><br />{submitted.submission_id}</div>
          {submitted.audit_id && <div><span className="text-[#9DB0B5]">Audit ID</span><br />{submitted.audit_id}</div>}
          {submitted.submitted_at && <div><span className="text-[#9DB0B5]">Submitted at (UTC)</span><br />{submitted.submitted_at}</div>}
        </div>
      )}
      <p className="text-[11px] text-[#6F828A] mt-6 leading-snug">
        Please keep the IDs above with your own records. You may now close this window.
      </p>
    </div>
  );
}

function VisitContextCard({ visit, visitWindow, invite }) {
  if (!visit) return null;
  return (
    <div className="border border-[#D9E1E3] rounded-md bg-[#F4FAFA] p-4" data-testid="fac-visit-context">
      <div className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">Visit you are observing</div>
      <div className="mt-1 text-sm font-medium text-[#2E3A3F]">
        {TYPE_LABEL[visit.visit_type] || visit.visit_type}
        {visit.evidence_ref && <span className="ml-2 font-mono-data text-[11px] text-[#9DB0B5]">{visit.evidence_ref}</span>}
      </div>
      <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs text-[#2E3A3F]">
        <li className="flex items-start gap-2"><Clock className="w-3.5 h-3.5 mt-0.5 text-[#6F828A]" />{visit.visit_date}{visitWindow ? ` · ${visitWindow}` : ""}</li>
        {visit.location && <li className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 mt-0.5 text-[#6F828A]" />{visit.location}</li>}
        {visit.children_first_names?.length ? (
          <li className="flex items-start gap-2"><Users className="w-3.5 h-3.5 mt-0.5 text-[#6F828A]" />Child(ren): {visit.children_first_names.join(", ")}</li>
        ) : null}
        {visit.facilitator_label && <li className="flex items-start gap-2"><FileText className="w-3.5 h-3.5 mt-0.5 text-[#6F828A]" />{visit.facilitator_label}</li>}
      </ul>
      {invite?.context_note && (
        <div className="mt-3 pt-3 border-t border-[#D9E1E3] text-xs text-[#2E3A3F]">
          <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] font-medium block mb-1">Note from the inviting parent</span>
          <p className="leading-relaxed">{invite.context_note}</p>
        </div>
      )}
    </div>
  );
}

function Section({ title, children, testId, intro }) {
  return (
    <section className="space-y-3" data-testid={testId}>
      <h2 className="text-xs uppercase tracking-[0.12em] text-[#6F828A] font-medium">{title}</h2>
      {intro && <p className="text-xs text-[#6F828A] leading-snug -mt-1">{intro}</p>}
      {children}
    </section>
  );
}

function Two({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, hint, children, testId }) {
  return (
    <div className="space-y-1" data-testid={testId}>
      <Label className="text-xs font-medium text-[#2E3A3F]">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-[#6F828A] leading-snug">{hint}</p>}
    </div>
  );
}
